import {
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  User as FbUser,
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Sign in to Firebase Auth using a backend-issued custom token.
 * Failure is logged but never throws — login flow must not break if Firebase
 * Auth is unavailable; caller falls back to REST-only mode.
 */
export async function signInToFirebase(customToken: string | undefined | null): Promise<void> {
  if (!customToken) {
    console.warn('[FirebaseAuthBridge] no custom token from backend; Firestore realtime will be disabled');
    return;
  }
  try {
    await signInWithCustomToken(auth, customToken);
  } catch (err) {
    console.error('[FirebaseAuthBridge] signInWithCustomToken failed:', err);
  }
}

/** Sign out from Firebase Auth. Swallows errors (logout flow must not break). */
export async function signOutFromFirebase(): Promise<void> {
  try {
    await signOut(auth);
  } catch (err) {
    console.error('[FirebaseAuthBridge] signOut failed:', err);
  }
}

/**
 * Resolve once onAuthStateChanged fires for the first time. Use this on
 * page load to wait for Firebase Auth's persistent session to restore
 * before deciding whether recovery is needed.
 *
 * Timeout fallback: if Firebase Auth fails to initialize (e.g. invalid/empty
 * apiKey) the callback never fires and the whole login flow hangs. We resolve
 * with null after the timeout so the rest of the app boots; Firestore-realtime
 * features will degrade gracefully but at least the page renders.
 */
export function waitForFirebaseAuthReady(timeoutMs = 3000): Promise<FbUser | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (user: FbUser | null) => {
      if (settled) return;
      settled = true;
      resolve(user);
    };

    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      finish(user);
    });

    setTimeout(() => {
      if (!settled) {
        console.warn('[FirebaseAuthBridge] auth state never fired within ' + timeoutMs + 'ms — proceeding without Firebase session');
        finish(null);
      }
    }, timeoutMs);
  });
}
